import { type TextInputSize } from "@/shared/foundation/primitives";

import type { FilterDefinition } from "./FilterDefinition";
import { type Filter, identityOperator } from "./FilterSchema";
import { BooleanValueInput } from "./valueInputs/BooleanValueInput";
import { DateValueInput } from "./valueInputs/DateValueInput";
import { NumericValueInput } from "./valueInputs/NumericValueInput";
import { OptionsValueInput } from "./valueInputs/OptionsValueInput";
import { StringValueInput } from "./valueInputs/StringValueInput";

interface FilterValueInputProps {
  filter: Filter;
  /** The definition `filter` was built from; supplies options for sets. */
  definition: FilterDefinition;
  onChange: (next: Filter["values"]) => void;
  /** Take focus on mount; off for an editor showing several at once. */
  autoFocus?: boolean;
  /** Marks the input invalid, for an error shown beside it. */
  invalid?: boolean;
  /**
   * The inputs' size, on the `TextInput` scale, to match the fields beside
   * it. Date pickers keep their own. @default "md"
   */
  size?: TextInputSize;
}

/**
 * The value input a filter's type calls for. The filter menu renders it in its
 * popover; an inline editor such as a trigger's condition cards renders the
 * same one.
 */
export function FilterValueInput({
  filter,
  definition,
  onChange,
  autoFocus = true,
  invalid = false,
  size = "md",
}: FilterValueInputProps) {
  return renderValueInput(filter, definition, onChange, {
    autoFocus,
    invalid,
    size,
  });
}

function renderValueInput(
  filter: Filter,
  selectedField: FilterDefinition,
  onChange: (next: Filter["values"]) => void,
  inputOptions: { autoFocus: boolean; invalid: boolean; size: TextInputSize },
) {
  switch (filter.type) {
    case "string":
      return (
        <StringValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          {...inputOptions}
        />
      );
    // `metric` shares the numeric value input. FilterForm isn't used to build
    // metric filters today (the metrics pane is bespoke), but the case keeps
    // the switch exhaustive over `Filter["type"]`.
    case "numeric":
    case "metric":
      return (
        <NumericValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          {...inputOptions}
        />
      );
    case "date":
      return (
        <DateValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          invalid={inputOptions.invalid}
        />
      );
    case "boolean":
      return (
        <BooleanValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          size={inputOptions.size}
        />
      );
    case "set":
    case "enum":
      return (
        <OptionsValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          size={inputOptions.size}
          invalid={inputOptions.invalid}
          // `filter.type` is seeded from the definition and the form remounts
          // per field, so a set/enum filter always has a set/enum definition.
          // The `[]` fallback is unreachable; it only narrows the type.
          // (`identity` also carries `options`, of a different shape — hence the
          // type check rather than an `in` check.)
          options={
            selectedField.type === "set" || selectedField.type === "enum"
              ? selectedField.options
              : []
          }
          // One option source per field. The form already remounts when the
          // selected field changes, so this is belt-and-braces — but the type
          // requires a key whenever `options` may be a resolver, and the field
          // is the honest answer.
          optionsKey={selectedField.field}
        />
      );
    case "identity": {
      // `filter.type` mirrors `selectedField.type` (both seeded from the same
      // definition), so this guard is unreachable — it narrows `selectedField`
      // to the identity variant for `options`.
      if (selectedField.type !== "identity") {
        return null;
      }
      // The operator names the principal type, and the type names its
      // directory. Both lookups are total for a comparator that came from
      // `effectiveComparators(selectedField)`, which only emits operators for
      // principal types this definition declares.
      const operator = identityOperator(filter.comparator);
      const typeOptions = selectedField.options.find(
        (option) => option.type === operator?.type,
      );
      if (typeOptions === undefined) {
        return null;
      }
      return (
        <OptionsValueInput
          // Switching operator moves to a different directory while staying on
          // one field, so `FilterForm` itself never remounts (it is keyed by
          // field). Keying the picker by principal type instead drops the
          // search text, the latched option set and the pinned rows together —
          // all of which were typed against, or fetched from, the old kind.
          key={typeOptions.type}
          // The principal type lives on the filter's comparator; within one
          // type, picking entities is plain multi-select equality.
          comparator="EQUALS"
          value={filter.values}
          onChange={onChange}
          size={inputOptions.size}
          invalid={inputOptions.invalid}
          options={typeOptions.optionResolver}
          // Keeps the two kinds' query entries distinct across that remount.
          optionsKey={typeOptions.type}
        />
      );
    }
  }
}
